/**
 * Category Template Service
 * Manages dynamic product categories and subcategories
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { adminSupabase, shouldUseAdminClient } from '@/integrations/supabase/adminClient';
import { logger } from '@/lib/logger';

export type DomainType = 'lighting_fixtures' | 'lighting_controls' | 'lv_products';

export interface CategoryTemplate {
  id: string;
  domain: DomainType;
  category: string;
  subcategory?: string;
  parent_category?: string;
  display_name: string;
  description?: string;
  icon_svg?: string;
  icon_url?: string;
  default_color: string;
  display_order: number;
  is_active: boolean;
  is_system: boolean;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Extended fields for custom icon support
  icon_type?: 'svg' | 'abbreviation';
  abbreviation?: string;
  fill_color?: string;
  text_color?: string;
  icon_shape?: 'square' | 'circle';
}

export interface CreateCategoryTemplateDto {
  domain: DomainType;
  category: string;
  subcategory?: string;
  parent_category?: string;
  display_name: string;
  description?: string;
  icon_svg?: string;
  icon_url?: string;
  default_color?: string;
  display_order?: number;
  is_active?: boolean;
  metadata?: Record<string, any>;
}

export interface UpdateCategoryTemplateDto extends Partial<CreateCategoryTemplateDto> {
  is_active?: boolean;
}

class CategoryTemplateService {
  /**
   * Get the appropriate Supabase client based on operation type
   */
  private async getClient(operation: 'read' | 'write' = 'read') {
    // For write operations in local dev, use admin client if available
    if (operation === 'write' && shouldUseAdminClient()) {
      logger.debug('Using admin client for write operation');
      if (adminSupabase) {
        return adminSupabase;
      }
    }
    // For all other cases, use regular client
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Get all category templates for a domain
   */
  async getByDomain(domain: DomainType): Promise<CategoryTemplate[]> {
    const client = await this.getClient('read');
    const { data, error } = await client
      .from('category_templates')
      .select('*')
      .eq('domain', domain)
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('display_name', { ascending: true });

    if (error) {
      logger.error('Error fetching category templates:', error);
      throw new Error(`Failed to fetch category templates: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all categories (no subcategories) for a domain
   */
  async getCategories(domain: DomainType): Promise<CategoryTemplate[]> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('category_templates')
      .select('*')
      .eq('domain', domain)
      .is('subcategory', null)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('Error fetching categories:', error);
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get subcategories for a specific category
   */
  async getSubcategories(domain: DomainType, category: string): Promise<CategoryTemplate[]> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('category_templates')
      .select('*')
      .eq('domain', domain)
      .eq('category', category)
      .not('subcategory', 'is', null)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('Error fetching subcategories:', error);
      throw new Error(`Failed to fetch subcategories: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get a specific category template
   */
  async getById(id: string): Promise<CategoryTemplate | null> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('category_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      logger.error('Error fetching category template:', error);
      throw new Error(`Failed to fetch category template: ${error.message}`);
    }

    return data;
  }

  /**
   * Get a category template by path
   */
  async getByPath(
    domain: DomainType,
    category: string,
    subcategory?: string,
  ): Promise<CategoryTemplate | null> {
    const supabase = await getAuthenticatedSupabaseClient();
    let query = supabase
      .from('category_templates')
      .select('*')
      .eq('domain', domain)
      .eq('category', category);

    if (subcategory) {
      query = query.eq('subcategory', subcategory);
    } else {
      query = query.is('subcategory', null);
    }

    const { data, error } = await query.single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      logger.error('Error fetching category by path:', error);
      return null;
    }

    return data;
  }

  /**
   * Create a new category template (platform admin only)
   */
  async create(dto: CreateCategoryTemplateDto): Promise<CategoryTemplate> {
    logger.log('Creating category template:', dto);

    // Clean up any escaped quotes in the SVG before saving
    const cleanedDto = { ...dto };
    if (cleanedDto.icon_svg) {
      cleanedDto.icon_svg = cleanedDto.icon_svg
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t');
    }

    const supabase = await this.getClient('write');
    const { data, error } = await supabase
      .from('category_templates')
      .insert({
        ...cleanedDto,
        default_color: cleanedDto.default_color || '#6B7280',
        display_order: cleanedDto.display_order || 999,
        is_active: cleanedDto.is_active !== false,
        metadata: cleanedDto.metadata || {},
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating category template:', error);
      throw new Error(`Failed to create category template: ${error.message}`);
    }

    return data;
  }

  /**
   * Update a category template (platform admin only)
   */
  async update(id: string, dto: UpdateCategoryTemplateDto): Promise<CategoryTemplate> {
    logger.log('CategoryTemplateService.update called with:', {
      id,
      hasIconSvg: !!dto.icon_svg,
      iconSvgLength: dto.icon_svg?.length,
      iconSvgFirst100: dto.icon_svg?.substring(0, 100),
      color: dto.default_color,
      metadata: dto.metadata,
    });

    // Clean up any escaped quotes in the SVG before saving
    const cleanedDto = { ...dto };
    if (cleanedDto.icon_svg) {
      // Store original length
      const originalLength = cleanedDto.icon_svg.length;

      cleanedDto.icon_svg = cleanedDto.icon_svg
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t');

      logger.log('SVG cleaned:', {
        originalLength,
        cleanedLength: cleanedDto.icon_svg.length,
        lengthDiff: originalLength - cleanedDto.icon_svg.length,
      });
    }

    // First, perform the update
    const writeClient = await this.getClient('write');
    const { error: updateError } = await writeClient
      .from('category_templates')
      .update(cleanedDto)
      .eq('id', id);

    if (updateError) {
      logger.error('Error updating category template:', updateError);
      throw new Error(`Failed to update category template: ${updateError.message}`);
    }

    // Then fetch the updated record separately to avoid RLS issues with chained select
    const readClient = await this.getClient('read');
    const { data, error: selectError } = await readClient
      .from('category_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (selectError) {
      logger.error('Error fetching updated category template:', selectError);
      // Even if select fails, the update succeeded, so return the partial data
      return { id, ...dto } as CategoryTemplate;
    }

    logger.log('Category template updated in database:', {
      id: data.id,
      hasIconSvg: !!data.icon_svg,
      iconSvgLength: data.icon_svg?.length,
      color: data.default_color,
    });

    return data;
  }

  /**
   * Delete a category template (platform admin only, non-system only)
   */
  async delete(id: string): Promise<void> {
    logger.log('Deleting category template:', id);

    const supabase = await this.getClient('write');
    const { error } = await supabase
      .from('category_templates')
      .delete()
      .eq('id', id)
      .eq('is_system', false);

    if (error) {
      logger.error('Error deleting category template:', error);
      throw new Error(`Failed to delete category template: ${error.message}`);
    }

    logger.log('Category template deleted successfully');
  }

  /**
   * Reorder categories
   */
  async reorder(updates: Array<{ id: string; display_order: number }>): Promise<void> {
    const supabase = await this.getClient('write');
    const promises = updates.map(({ id, display_order }) =>
      supabase.from('category_templates').update({ display_order }).eq('id', id),
    );

    const results = await Promise.all(promises);
    const errors = results.filter(r => r.error);

    if (errors.length > 0) {
      logger.error('Errors reordering categories:', errors);
      throw new Error('Failed to reorder categories');
    }
  }

  /**
   * Upload and save an SVG icon for a category
   */
  async updateIcon(id: string, svgContent: string): Promise<CategoryTemplate> {
    logger.log('Updating icon for category:', id);

    // Clean up any escaped quotes in the SVG before saving
    const cleanedSvg = svgContent
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t');

    // Perform the update
    const writeClient = await this.getClient('write');
    const { error: updateError } = await writeClient
      .from('category_templates')
      .update({ icon_svg: cleanedSvg })
      .eq('id', id);

    if (updateError) {
      logger.error('Error updating category icon:', updateError);
      throw new Error(`Failed to update category icon: ${updateError.message}`);
    }

    // Fetch the updated record separately
    const readClient = await this.getClient('read');
    const { data, error: selectError } = await readClient
      .from('category_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (selectError) {
      logger.error('Error fetching updated category after icon update:', selectError);
      // Return partial data if select fails
      return { id, icon_svg: svgContent } as CategoryTemplate;
    }

    return data;
  }

  /**
   * Update the default color for a category
   */
  async updateColor(id: string, color: string): Promise<CategoryTemplate> {
    logger.log('Updating color for category:', id, 'to', color);

    // Perform the update
    const writeClient = await this.getClient('write');
    const { error: updateError } = await writeClient
      .from('category_templates')
      .update({ default_color: color })
      .eq('id', id);

    if (updateError) {
      logger.error('Error updating category color:', updateError);
      throw new Error(`Failed to update category color: ${updateError.message}`);
    }

    // Fetch the updated record separately
    const readClient = await this.getClient('read');
    const { data, error: selectError } = await readClient
      .from('category_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (selectError) {
      logger.error('Error fetching updated category after color update:', selectError);
      // Return partial data if select fails
      return { id, default_color: color } as CategoryTemplate;
    }

    return data;
  }

  /**
   * Get category hierarchy for a domain
   */
  async getCategoryHierarchy(domain: DomainType): Promise<Map<string, CategoryTemplate[]>> {
    const templates = await this.getByDomain(domain);
    const hierarchy = new Map<string, CategoryTemplate[]>();

    templates.forEach(template => {
      const key = template.subcategory ? template.category : 'root';
      if (!hierarchy.has(key)) {
        hierarchy.set(key, []);
      }

      if (template.subcategory || !template.parent_category) {
        hierarchy.get(key)!.push(template);
      }
    });

    return hierarchy;
  }

  /**
   * Clone a category template
   */
  async clone(id: string, newName: string): Promise<CategoryTemplate> {
    const original = await this.getById(id);
    if (!original) {
      throw new Error('Category template not found');
    }

    const { id: _, created_at, updated_at, is_system, ...cloneData } = original;

    return this.create({
      ...cloneData,
      display_name: newName,
      display_order: original.display_order + 1,
    });
  }

  /**
   * Bulk create categories (for initial setup or import)
   */
  async bulkCreate(categories: CreateCategoryTemplateDto[]): Promise<CategoryTemplate[]> {
    const supabase = await this.getClient('write');
    const { data, error } = await supabase
      .from('category_templates')
      .insert(
        categories.map(cat => ({
          ...cat,
          default_color: cat.default_color || '#6B7280',
          display_order: cat.display_order || 999,
          is_active: cat.is_active !== false,
          metadata: cat.metadata || {},
        })),
      )
      .select();

    if (error) {
      logger.error('Error bulk creating categories:', error);
      throw new Error(`Failed to bulk create categories: ${error.message}`);
    }

    return data || [];
  }
}

export const categoryTemplateService = new CategoryTemplateService();
